import { z } from 'zod';

import { zodExportAttemptSchema } from '../exportAttempt/validations';
import { INVOICE_FILE_ROLES } from '../useQueryInvoiceFileByRole/constants';
import {
  INVOICE_V2_SOURCE,
  INVOICE_V2_STATUS,
} from '../useQueryInvoicesV2/constants';

const zodDecimalSchema = z.string().nullish();
const zodDateSchema = z.iso.date().nullish();
const zodDateTimeSchema = z.iso.datetime({ offset: true }).nullish();

const zodBilledByShareSchema = z.object({
  invoiceId: z.uuid(),
  invoiceNumber: z.string().nullish(),
  quantity: z.string(),
});

const zodDeliveryNoteQuantitySchema = z.object({
  accepted: z.string(),
  alreadyBilled: z.string(),
  billedBy: z.array(zodBilledByShareSchema),
  delivered: z.string(),
  deliveryDate: zodDateSchema,
  deliveryNoteId: z.uuid(),
  deliveryNoteNumber: z.string(),
});

const zodQuantityCheckDetailsSchema = z.object({
  acceptedTotal: z.string(),
  actual: z.string().nullish(),
  alreadyBilledTotal: z.string(),
  available: z.string(),
  deliveryNotes: z.array(zodDeliveryNoteQuantitySchema),
  expected: z.string().nullish(),
  invoiced: z.string(),
  unit: z.string(),
});

const zodExpectedActualDetailsSchema = z
  .object({
    actual: z.string().nullish(),
    expected: z.string().nullish(),
  })
  .catchall(z.json());

const zodArithmeticCheckDetailsSchema = z.object({
  actual: z.string(),
  expected: z.string(),
  tolerance: z.string(),
});

const zodDuplicateInvoiceDetailsSchema = z.object({
  duplicateInvoiceIds: z.array(z.uuid()),
});

const zodItemNetPriceDetailsSchema = z.object({
  contractId: z.uuid(),
  contractItemId: z.uuid(),
  expectedUnit: z.string().nullish(),
  expectedUnitPrice: z.string(),
  invoiceUnit: z.string().nullish(),
  invoiceUnitPrice: z.string(),
});

export const zodTypedCheckResultDetailsSchema = z.union([
  zodQuantityCheckDetailsSchema,
  zodArithmeticCheckDetailsSchema,
  zodDuplicateInvoiceDetailsSchema,
  zodItemNetPriceDetailsSchema,
  zodExpectedActualDetailsSchema,
]);

const zodCheckResultSchema = z.object({
  checkId: z.string(),
  description: z.string().nullish(),
  details: zodTypedCheckResultDetailsSchema.nullish(),
  fieldId: z.string(),
  headline: z.string().nullish(),
  norm: z.string().nullish(),
  resultCode: z.string(),
  severity: z.enum(['pass', 'warn', 'fail', 'skip']),
  skippedPrerequisite: z.record(z.string(), z.json()).nullish(),
  subjectId: z.string().nullish(),
  // Open contract: an unknown check kind must not fail the whole invoice.
  subjectKind: z.string(),
});

export const zodCheckRunSchema = z.object({
  createdOn: z.iso.datetime({ offset: true }),
  id: z.uuid(),
  overallStatus: z.enum(['green', 'yellow', 'red']).nullish(),
  results: z.array(zodCheckResultSchema).optional(),
  sequence: z.number().int(),
  status: z.enum(['queued', 'running', 'completed', 'failed']),
  // Open contract: an unknown trigger must not fail the whole invoice.
  trigger: z.string(),
});

const zodChargeOrAllowanceSchema = z.object({
  absolutePerItem: zodDecimalSchema,
  absolutePerItemApproved: zodDecimalSchema,
  absoluteTotal: zodDecimalSchema,
  absoluteTotalApproved: zodDecimalSchema,
  basisAmount: zodDecimalSchema,
  basisAmountApproved: zodDecimalSchema,
  name: z.string().nullable(),
  nameApproved: z.string().nullable(),
  percentage: zodDecimalSchema,
  percentageApproved: zodDecimalSchema,
});

const zodMatchedDeliveryNoteItemSchema = z.object({
  linkId: z.uuid().nullish(),
  name: z.string().nullish(),
  position: z.string().nullish(),
  quantity: zodDecimalSchema,
  sellerAssignedId: z.string().nullish(),
  source: z.string().nullish(),
  unit: z.string().nullish(),
  unitFits: z.boolean().nullish(),
});

const zodDeliveryNoteRefSchema = z.object({
  cited: z.boolean().nullish(),
  deliveryDate: zodDateSchema,
  deliveryNoteId: z.uuid().nullish(),
  deliveryNoteNumber: z.string().nullish(),
  match: z
    .object({ items: z.array(zodMatchedDeliveryNoteItemSchema) })
    .nullish(),
});

const zodSkontoTermSchema = z.object({
  dueAmount: z.string(),
  dueAmountApproved: zodDecimalSchema,
  dueDate: zodDateSchema,
  percentage: z.string(),
  percentageApproved: zodDecimalSchema,
});

const zodTaxCalculationSchema = z.object({
  basisAmount: z.string(),
  basisAmountApproved: zodDecimalSchema,
  category: z.string().nullish(),
  currency: z.string().default('EUR'),
  percent: z.string(),
  percentApproved: zodDecimalSchema,
  taxAmount: z.string(),
  taxAmountApproved: zodDecimalSchema,
  type: z.literal('VAT').default('VAT'),
});

const zodInvoiceDataSchema = z.object({
  allowances: z.array(zodChargeOrAllowanceSchema).nullable(),
  charges: z.array(zodChargeOrAllowanceSchema).nullable(),
  currency: z.string().nullable(),
  dueAmount: zodDecimalSchema,
  dueAmountApproved: zodDecimalSchema,
  dueDate: zodDateSchema,
  generalAllowance: zodDecimalSchema,
  generalAllowanceApproved: zodDecimalSchema,
  grossAmount: zodDecimalSchema,
  grossAmountApproved: zodDecimalSchema,
  iban: z.string().nullable(),
  ibanApproved: z.string().nullable(),
  invoiceDate: zodDateSchema,
  invoiceNumber: z.string().nullable(),
  logisticFees: zodDecimalSchema,
  logisticFeesApproved: zodDecimalSchema,
  netAmount: zodDecimalSchema,
  netAmountApproved: zodDecimalSchema,
  prepaid: zodDecimalSchema,
  prepaidApproved: zodDecimalSchema,
  receiptDate: zodDateTimeSchema,
  skonto: z.array(zodSkontoTermSchema).nullable(),
  taxAmount: zodDecimalSchema,
  taxAmountApproved: zodDecimalSchema,
  taxCalculation: z.array(zodTaxCalculationSchema).nullable(),
});

const zodLineItemSchema = z.object({
  accountingReferenceIds: z.array(z.uuid()).nullable(),
  additionalQuantity: zodDecimalSchema,
  additionalQuantityApproved: zodDecimalSchema,
  additionalUnitType: z.string().nullable(),
  additionalUnitTypeApproved: z.string().nullable(),
  allowances: z.array(zodChargeOrAllowanceSchema).nullable(),
  applicableTax: z.string().nullable(),
  applicableTaxApproved: z.string().nullable(),
  buyerOrder: z.string().nullable(),
  charges: z.array(zodChargeOrAllowanceSchema).nullable(),
  contractId: z.uuid().nullable(),
  costCenter: z.string().nullable(),
  currency: z.string().nullable(),
  deliveryNotes: z.array(zodDeliveryNoteRefSchema).nullable(),
  description: z.string().nullable(),
  grossPrice: zodDecimalSchema,
  grossPriceApproved: zodDecimalSchema,
  id: z.uuid(),
  name: z.string().nullable(),
  nameApproved: z.string().nullable(),
  netPricePerQuantity: zodDecimalSchema,
  netPricePerQuantityApproved: zodDecimalSchema,
  note: z.string().nullable(),
  position: z.number().int().nullable(),
  quantity: zodDecimalSchema,
  quantityApproved: zodDecimalSchema,
  sellerAssignedId: z.string().nullable(),
  sellerOrder: z.string().nullable(),
  taxPercent: zodDecimalSchema,
  taxPercentApproved: zodDecimalSchema,
  total: zodDecimalSchema,
  totalApproved: zodDecimalSchema,
  unit: z.string().nullable(),
  unitApproved: z.string().nullable(),
  unitPrice: zodDecimalSchema,
  unitPriceApproved: zodDecimalSchema,
});

const zodPartySchema = z.object({
  buildingNumber: z.string().nullish(),
  city: z.string().nullish(),
  companyAccountId: z.uuid().nullish(),
  companyId: z.uuid().nullish(),
  country: z.string().nullish(),
  email: z.string().nullish(),
  id: z.uuid(),
  issuerAssignedId: z.string().nullish(),
  name: z.string().nullish(),
  postCode: z.string().nullish(),
  resolutionSource: z
    .enum(['user_selected', 'email_sender', 'fuzzy_fallback', 'unresolved'])
    .nullish(),
  role: z.enum(['issuer', 'seller', 'buyer']).nullish(),
  streetName: z.string().nullish(),
  taxId: z.string().nullish(),
});

const zodFileSchema = z.object({
  contentType: z.string().nullish(),
  fileId: z.string(),
  filename: z.string().nullish(),
  id: z.uuid(),
  role: z.enum(INVOICE_FILE_ROLES),
});

export const zodUserFileSchema = z.object({
  contentType: z.string().nullish(),
  fileId: z.string(),
  filename: z.string().nullish(),
  id: z.uuid(),
  role: z.enum(['misc', 'performance_record']),
});

export const zodInvoiceAssigneeSchema = z.object({
  assignedById: z.uuid(),
  assignedOn: z.iso.datetime({ offset: true }),
  email: z.string(),
  firstName: z.string().nullish(),
  id: z.uuid(),
  lastName: z.string().nullish(),
});

export const zodInvoiceDetailSchema = z.object({
  assignees: z.array(zodInvoiceAssigneeSchema),
  data: zodInvoiceDataSchema.nullish(),
  exportAttempts: z.array(zodExportAttemptSchema),
  exportSummary: z
    .enum(['none', 'pending', 'succeeded', 'partial_failure', 'failed'])
    .nullish(),
  files: z.array(zodFileSchema),
  id: z.uuid(),
  latestCheckRun: zodCheckRunSchema.nullish(),
  lineItems: z.array(zodLineItemSchema),
  ownerCompanyAccountId: z.uuid(),
  ownerCompanyId: z.uuid().nullish(),
  parties: z.array(zodPartySchema),
  receiptDate: zodDateTimeSchema,
  resultStatus: z.enum(['open', 'completed', 'rejected']),
  source: z.enum(INVOICE_V2_SOURCE),
  status: z.enum(INVOICE_V2_STATUS),
  userFiles: z.array(zodUserFileSchema),
});
